import { Component, inject, OnDestroy, OnInit} from '@angular/core'
import { ActivatedRoute, Event, NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { delay, filter, map, Subject, take, takeUntil, tap } from 'rxjs'
import { MarketingMetricsStoreService } from '@features/marketing-metrics-store/marketing-metrics-store.service'
import { ViewportScroller } from '@angular/common'
import { getRouterNavigationState } from '@sun-finance/utils'
import { ProgressService } from '@features/progress/progress.service'
import { AddProgressStep } from '@features/progress/progress.model'
import { UI_STEP_METADATA_STATE_KEY, UI_STEP_NAME_NAVIGATION_STATE_KEY } from '@features/progress/contracts'
import { ChatDirective } from '@features/chat/chat.directive'
import { HeaderComponent } from '@features/header/header.component'
import { FooterComponent } from '@features/footer/footer.component'
import { CookieOverlayComponent } from '@features/cookie-consents/cookie-overlay/cookie-overlay.component'
import { GoogleTagManagerWrapperDirective } from '@features/google-tag-manager-wrapper.directive'
import { PaymentStatusHelper } from '@features/open-banking/status/status-helper.service'
import { PaymentStatusComponent } from '@features/open-banking/status/payment-status/payment-status.component'
import { MatDialog } from '@angular/material/dialog'
import { RisksIntegrationsService } from '@features/risks-integrations/risks-integrations.service'
import { PROJECT } from '@app/tokens'
import { GoogleRecaptchaWrapperDirective } from "@features/recaptcha/google-recaptcha-wrapper.directive"

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, ChatDirective, HeaderComponent, FooterComponent, CookieOverlayComponent, GoogleTagManagerWrapperDirective, GoogleRecaptchaWrapperDirective]
})
export class AppComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>()

  constructor(private metricsStore: MarketingMetricsStoreService,
              private route: ActivatedRoute,
              private router: Router,
              private viewportScroller: ViewportScroller,
              private progressService: ProgressService,
              private paymentStatusHelper: PaymentStatusHelper,
              private dialog: MatDialog,
              private risksIntegrations: RisksIntegrationsService,
  ) {
    viewportScroller.setHistoryScrollRestoration('manual')
    document.body.id = `${inject(PROJECT)}-app`
  }

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        filter(params => params.keys.some(key => key.toLowerCase().startsWith('utm_'))),
        map(_ => this.route.snapshot.queryParams),
        takeUntil(this.onDestroy$),
      ).subscribe(params => this.metricsStore.store(params))

    const events$ = this.router.events

    events$
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        delay(200),
        takeUntil(this.onDestroy$),
      )
      // Do some side effect on each navigation
      .subscribe(() => {
        this.viewportScroller.scrollToPosition([0, 0])

        let route = this.route
        while (route.firstChild) {
          route = route.firstChild
        }

        // This is to prevent race condition between submit and init for registration and lead pages, they control device data collect init and submit by themselves.
        if(!route.snapshot.data['disableDeviceDataSubmissionFallback']) {
          // Fallback, we retry to submit data if on every route change, if it is still in storage(Means it was collected but not submitted).
          this.risksIntegrations.submitCollectedDeviceData()
        }
      })

    events$
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        map(({ urlAfterRedirects }): AddProgressStep => {
          const state = getRouterNavigationState(this.router)
          return {
            action: state?.[UI_STEP_NAME_NAVIGATION_STATE_KEY],
            metadata: { ...(state?.[UI_STEP_METADATA_STATE_KEY] ?? {}), urlAfterRedirects, uiState: true },
          }
        }),
        filter(c => Boolean(c.action)),
        takeUntil(this.onDestroy$),
      )
      .subscribe(step => {
        this.progressService.addStep(step).pipe(take(1)).subscribe()
      })

    this.paymentStatusHelper.isShown$.pipe(
      takeUntil(this.onDestroy$),
      filter(Boolean),
      tap(() => this.dialog.open(PaymentStatusComponent, {width: '800px', panelClass: ''}))).subscribe()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

}
